import React, { useMemo, useState, createElement } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import PageHeader from '../components/PageHeader';
import MetricCard from '../components/MetricCard';
import SegmentDetailDrawer from '../components/drawers/SegmentDetailDrawer';
import { useAdminData } from '../contexts/AdminDataContext';
import { Users, Activity, DollarSign, TrendingUp, Download, TrendingDown } from 'lucide-react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
type PeriodFilter = '7days' | '30days' | '90days' | 'thisMonth';
const PlatformAnalytics = () => {
  const {
    platformAnalytics,
    showToast
  } = useAdminData();
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('30days');
  const [selectedSegment, setSelectedSegment] = useState<any>(null);
  const [showSegmentDrawer, setShowSegmentDrawer] = useState(false);
  // Filter data based on period
  const filteredData = useMemo(() => {
    const today = new Date();
    let daysToShow = 30;
    switch (periodFilter) {
      case '7days':
        daysToShow = 7;
        break;
      case '30days':
        daysToShow = 30;
        break;
      case '90days':
        daysToShow = 90;
        break;
      case 'thisMonth':
        daysToShow = today.getDate();
        break;
    }
    const cutoffDate = new Date(today);
    cutoffDate.setDate(cutoffDate.getDate() - daysToShow);
    return {
      usersSeries: platformAnalytics.usersSeries.filter(item => new Date(item.date) >= cutoffDate),
      revenueSeries: platformAnalytics.revenueSeries.filter(item => new Date(item.date) >= cutoffDate)
    };
  }, [periodFilter, platformAnalytics]);
  const handleExportReport = () => {
    // Mock CSV export
    const csvContent = [['Date', 'Users', 'Revenue'], ...filteredData.usersSeries.map((item, idx) => [item.date, item.value, filteredData.revenueSeries[idx]?.value || 0])].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv'
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `platform-analytics-${periodFilter}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    showToast('Report esportato con successo!');
  };
  const handleSegmentClick = (segment: any) => {
    setSelectedSegment(segment);
    setShowSegmentDrawer(true);
  };
  const handleCloseSegmentDrawer = () => {
    setShowSegmentDrawer(false);
    setTimeout(() => setSelectedSegment(null), 300);
  };
  return <div className="flex h-screen bg-gray-50" data-id="element-3448">
      <div className="w-20 flex-shrink-0" data-id="element-3449">
        <Sidebar data-id="element-3450" />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-3451">
        <Topbar data-id="element-3452" />
        <main className="flex-1 overflow-y-auto p-4 md:p-6" data-id="element-3453">
          {/* Header with filters */}
          <div className="flex items-center justify-between mb-6" data-id="element-3454">
            <div data-id="element-3455">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-3456">
                Analitiche Piattaforma
              </h1>
              <p className="text-sm text-gray-500 mt-1" data-id="element-3457">
                Panoramica completa delle performance della piattaforma
              </p>
            </div>
            <div className="flex items-center gap-3" data-id="element-3458">
              <select value={periodFilter} onChange={e => setPeriodFilter(e.target.value as PeriodFilter)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-600" data-id="element-3459">
                <option value="7days" data-id="element-3460">Ultimi 7 giorni</option>
                <option value="30days" data-id="element-3461">Ultimi 30 giorni</option>
                <option value="90days" data-id="element-3462">Ultimi 90 giorni</option>
                <option value="thisMonth" data-id="element-3463">Questo mese</option>
              </select>
              <button onClick={handleExportReport} className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors" data-id="element-3464">
                <Download className="h-4 w-4 mr-2" data-id="element-3465" />
                Esporta Report
              </button>
            </div>
          </div>

          {/* KPI Cards */}
          <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-3466">
            <MetricCard title="Utenti Totali" value={platformAnalytics.kpis.totalUsers.toLocaleString()} change="12.5%" isPositive={true} icon={Users} data-id="element-3467" />
            <MetricCard title="Engagement Rate" value={`${platformAnalytics.kpis.engagementRate}%`} change="8.3%" isPositive={true} icon={Activity} data-id="element-3468" />
            <MetricCard title="Revenue" value={`€${platformAnalytics.kpis.revenue.toLocaleString()}`} change="15.7%" isPositive={true} icon={DollarSign} data-id="element-3469" />
            <MetricCard title="Retention" value={`${platformAnalytics.kpis.retention}%`} change="3.2%" isPositive={true} icon={TrendingUp} data-id="element-3470" />
          </div>

          {/* Charts Row */}
          <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-2" data-id="element-3471">
            {/* User Growth Chart */}
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm" data-id="element-3472">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-3473">
                Crescita Utenti
              </h3>
              <div className="h-80" data-id="element-3474">
                <ResponsiveContainer width="100%" height="100%" data-id="element-3475">
                  <LineChart data={filteredData.usersSeries} data-id="element-3476">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" data-id="element-3477" />
                    <XAxis dataKey="date" stroke="#6B7280" fontSize={12} tickFormatter={value => {
                    const date = new Date(value);
                    return `${date.getDate()}/${date.getMonth() + 1}`;
                  }} data-id="element-3478" />
                    <YAxis stroke="#6B7280" fontSize={12} data-id="element-3479" />
                    <Tooltip contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '0.5rem'
                  }} labelFormatter={value => {
                    const date = new Date(value);
                    return date.toLocaleDateString('it-IT');
                  }} formatter={(value: any) => [value.toLocaleString(), 'Utenti']} data-id="element-3480" />
                    <Line type="monotone" dataKey="value" stroke="#108d3e" strokeWidth={2} dot={false} name="Utenti" data-id="element-3481" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Revenue Trend Chart */}
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm" data-id="element-3482">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-3483">
                Trend Revenue
              </h3>
              <div className="h-80" data-id="element-3484">
                <ResponsiveContainer width="100%" height="100%" data-id="element-3485">
                  <AreaChart data={filteredData.revenueSeries} data-id="element-3486">
                    <defs data-id="element-3487">
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1" data-id="element-3488">
                        <stop offset="5%" stopColor="#108d3e" stopOpacity={0.3} data-id="element-3489" />
                        <stop offset="95%" stopColor="#108d3e" stopOpacity={0} data-id="element-3490" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" data-id="element-3491" />
                    <XAxis dataKey="date" stroke="#6B7280" fontSize={12} tickFormatter={value => {
                    const date = new Date(value);
                    return `${date.getDate()}/${date.getMonth() + 1}`;
                  }} data-id="element-3492" />
                    <YAxis stroke="#6B7280" fontSize={12} data-id="element-3493" />
                    <Tooltip contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '0.5rem'
                  }} labelFormatter={value => {
                    const date = new Date(value);
                    return date.toLocaleDateString('it-IT');
                  }} formatter={(value: any) => [`€${value.toLocaleString()}`, 'Revenue']} data-id="element-3494" />
                    <Area type="monotone" dataKey="value" stroke="#108d3e" strokeWidth={2} fillOpacity={1} fill="url(#colorRevenue)" name="Revenue" data-id="element-3495" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Segment Breakdown Table */}
          <div className="rounded-lg border border-gray-200 bg-white overflow-hidden" data-id="element-3496">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-3497">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-3498">
                Breakdown per Segmento
              </h3>
              <p className="text-sm text-gray-500 mt-1" data-id="element-3499">
                Analisi dettagliata per canale di acquisizione
              </p>
            </div>
            <div className="overflow-x-auto" data-id="element-3500">
              <table className="w-full" data-id="element-3501">
                <thead data-id="element-3502">
                  <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3503">
                    <th className="px-6 py-3" data-id="element-3504">Segmento</th>
                    <th className="px-6 py-3" data-id="element-3505">Utenti</th>
                    <th className="px-6 py-3" data-id="element-3506">Engagement</th>
                    <th className="px-6 py-3" data-id="element-3507">Revenue</th>
                    <th className="px-6 py-3" data-id="element-3508">Trend</th>
                    <th className="px-6 py-3 text-right" data-id="element-3509">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-3510">
                  {platformAnalytics.segments.map(segment => <tr key={segment.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleSegmentClick(segment)} data-id="element-3511">
                      <td className="px-6 py-4" data-id="element-3512">
                        <div className="font-medium text-gray-900" data-id="element-3513">
                          {segment.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 font-medium text-gray-900" data-id="element-3514">
                        {segment.users.toLocaleString()}
                      </td>
                      <td className="px-6 py-4" data-id="element-3515">
                        <div className="flex items-center" data-id="element-3516">
                          <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2 max-w-[100px]" data-id="element-3517">
                            <div className="bg-green-600 h-2 rounded-full" style={{
                          width: `${segment.engagement}%`
                        }} data-id="element-3518" />
                          </div>
                          <span className="text-sm font-medium text-gray-900" data-id="element-3519">
                            {segment.engagement}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 font-medium text-gray-900" data-id="element-3520">
                        €{segment.revenue.toLocaleString()}
                      </td>
                      <td className="px-6 py-4" data-id="element-3521">
                        <div className="flex items-center" data-id="element-3522">
                          {segment.trend > 0 ? <TrendingUp className="h-4 w-4 text-green-600 mr-1" data-id="element-3523" /> : <TrendingDown className="h-4 w-4 text-red-600 mr-1" data-id="element-3524" />}
                          <span className={`text-sm font-medium ${segment.trend > 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-3525">
                            {segment.trend > 0 ? '+' : ''}
                            {segment.trend}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-3526">
                        <button onClick={e => {
                      e.stopPropagation();
                      handleSegmentClick(segment);
                    }} className="text-green-600 hover:text-green-700 font-medium text-sm" data-id="element-3527">
                          Dettagli
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>

      {/* Segment Detail Drawer */}
      <SegmentDetailDrawer isOpen={showSegmentDrawer} onClose={handleCloseSegmentDrawer} segment={selectedSegment} data-id="element-3528" />
    </div>;
};
export default PlatformAnalytics;